import { z } from 'zod';
import { DashboardTableStatusTone } from '@shigo-ui/dashboard-ui';

import { formatDisplayDate } from '../../../lib/format';
import { dashboardT } from '../../../lib/messages';

export enum SupplyStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
}

const namedEntitySchema = z.looseObject({ id: z.string(), name: z.string() });
const nullableString = z.string().nullable().optional();

const supplyAppContractSchema = z.looseObject({
  id: nullableString,
  appId: nullableString,
  name: nullableString,
  status: nullableString,
  appStoreId: nullableString,
  appStoreUrl: nullableString,
  bundleId: nullableString,
  domain: nullableString,
  platform: namedEntitySchema.nullable().optional(),
  iabCategories: z.array(z.string()).nullable().optional(),
  iabCategoryNames: z.array(z.string()).nullable().optional(),
  createdOn: nullableString,
});

export const supplyAppSchema = supplyAppContractSchema.transform((app) => ({
  ...app,
  id: app.id ?? app.appId ?? app.appStoreId ?? app.bundleId ?? app.name ?? '',
  name: app.name ?? app.bundleId ?? app.appStoreId ?? '',
  status: app.status ?? undefined,
  appStoreId: app.appStoreId ?? undefined,
  appStoreUrl: app.appStoreUrl ?? undefined,
  bundleId: app.bundleId ?? undefined,
  domain: app.domain ?? undefined,
  platform: app.platform ?? undefined,
  iabCategories: app.iabCategories ?? [],
  ...(app.iabCategoryNames ? { iabCategoryNames: app.iabCategoryNames } : {}),
  createdOn: app.createdOn ?? undefined,
}));

const supplyDomainContractSchema = z.looseObject({
  id: z.string(),
  url: z.string(),
  status: nullableString,
  iabCategories: z.array(z.string()).nullable().optional(),
  createdOn: nullableString,
});

export const supplyDomainSchema = supplyDomainContractSchema.transform((domain) => ({
  ...domain,
  iabCategories: domain.iabCategories ?? [],
}));

const tailoredSupplyContractSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  description: nullableString,
  appsCount: z.number().nullable().optional(),
  domainsCount: z.number().nullable().optional(),
  createdOn: nullableString,
});

export const tailoredSupplySchema = tailoredSupplyContractSchema;

export const supplyAppSelectionSchema = supplyAppContractSchema.extend({
  id: z.string().trim().min(1, dashboardT('supply.required')),
  name: z.string().trim().min(1, dashboardT('supply.required')),
});
export const supplyDomainSelectionSchema = supplyDomainContractSchema.extend({
  id: z.string().trim().min(1, dashboardT('supply.required')),
  url: z.string().trim().min(1, dashboardT('supply.required')),
});
export const tailoredSupplySelectionSchema = tailoredSupplyContractSchema.extend({
  id: z.string().trim().min(1, dashboardT('supply.required')),
  name: z.string().trim().min(1, dashboardT('supply.required')),
});

export const tailoredSupplyDetailSchema = z.looseObject({
  id: z.string().optional(),
  name: z.string(),
  description: nullableString,
  apps: z.array(supplyAppSchema).nullable().optional(),
  domains: z.array(supplyDomainSchema).nullable().optional(),
});

export const supplyReferenceSchema = z
  .looseObject({
    id: z.string().optional(),
    name: z.string().optional(),
    code: z.string().optional(),
    titlev2: z.string().optional(),
  })
  .transform((item) => ({
    id: item.id ?? item.code ?? item.name ?? '',
    name: item.titlev2 ?? item.name ?? item.code ?? item.id ?? '',
  }));

export const supplyReferenceListSchema = z
  .union([
    z.array(supplyReferenceSchema),
    z.looseObject({ content: z.array(supplyReferenceSchema).default([]) }),
  ])
  .transform((value) => (Array.isArray(value) ? value : value.content));

export const supplyPageSchema = <TSchema extends z.ZodType>(schema: TSchema) =>
  z.looseObject({
    content: z.array(schema).default([]),
    totalElements: z.number().default(0),
    totalPages: z.number().optional(),
    number: z.number().optional(),
  });

export type SupplyApp = z.infer<typeof supplyAppSchema>;
export type SupplyDomain = z.infer<typeof supplyDomainSchema>;
export type TailoredSupply = z.infer<typeof tailoredSupplySchema>;
export type SupplyAppSelection = z.infer<typeof supplyAppSelectionSchema>;
export type SupplyDomainSelection = z.infer<typeof supplyDomainSelectionSchema>;
export type TailoredSupplySelection = z.infer<typeof tailoredSupplySelectionSchema>;
export type TailoredSupplyDetail = z.infer<typeof tailoredSupplyDetailSchema>;
export type SupplyReference = z.infer<typeof supplyReferenceSchema>;
export type SupplyPage<TItem> = { content: TItem[]; totalElements: number };

export type SupplyListFilter = {
  searchTerm: string;
  page: number;
  size: number;
  companyIds?: readonly string[] | undefined;
};
export type SupplyAppPayload = {
  name: string;
  status: SupplyStatus;
  bundleId: string;
  appStoreUrl?: string;
  domain?: string;
  platform: { id: string; name: string };
  iabCategories: string[];
  appStoreId?: string;
};
export type SupplyDomainPayload = { url: string; status: SupplyStatus; iabCategories: string[] };
export type TailoredSupplyPayload = {
  id?: string;
  name: string;
  description: string;
  apps: Array<{ id: string }>;
  domains: Array<{ id: string }>;
};

export const supplyPageSizes = [5, 10, 25, 50, 100] as const;
export const defaultSupplyListFilter: SupplyListFilter = { searchTerm: '', page: 0, size: 10 };

export function serializeSupplyListFilter(filter: SupplyListFilter) {
  const params = new URLSearchParams({
    searchTerm: filter.searchTerm,
    page: String(filter.page),
    size: String(filter.size),
    sortProperty: 'createdOn',
    sortDirection: 'DESC',
  });
  if (filter.companyIds) params.set('companyIds', filter.companyIds.join(','));
  return params;
}

export function serializeTailoredSupplyListFilter(filter: SupplyListFilter) {
  const searchParams = serializeSupplyListFilter(filter);
  searchParams.set('companyIds', filter.companyIds?.join(',') ?? '');
  return searchParams;
}

export function formatSupplyDate(value: string | null | undefined) {
  return formatDisplayDate(value);
}

export function resolveSupplyStatusLabel(status: string | null | undefined) {
  if (status === SupplyStatus.Active) return dashboardT('supply.active');
  if (status === SupplyStatus.Archived) return dashboardT('supply.archived');
  return status ?? '-';
}

export function resolveSupplyStatusTone(status: string | null | undefined) {
  if (status === SupplyStatus.Active) return DashboardTableStatusTone.Success;
  if (status === SupplyStatus.Archived) return DashboardTableStatusTone.Neutral;
  return DashboardTableStatusTone.Info;
}
